/* eslint-disable max-lines-per-function */
import React from "react";
import { Form, Formik, FormikConfig } from "formik";
import { Grid, Typography } from "@mui/material";
import * as y from "yup";
import { HistoryBackButton } from "components/buttons";
import { SubmitButton } from "components/haven-design/form";
import { MaterialFormikSelectAutocomplete } from "components/haven-design/form/select";
import { InputWithErrorLabel as Input } from "components/haven-design/form/inputs";
import { PricingTiersFields } from "./pricing-tiers-fields";
import { PricingCalculator } from "./pricing-calculator";

/** Matches server seed `PricingType.Query.backfill` */
const PRICING_TYPE_FLAT_ID = 1;
const PRICING_TYPE_PER_UNIT_ID = 2;

const minimumPriceFieldLabel = (pricingTypeId: number | undefined): string => {
  if (pricingTypeId === PRICING_TYPE_FLAT_ID) return "Flat monthly plan price ($)";
  if (pricingTypeId === PRICING_TYPE_PER_UNIT_ID) return "Minimum monthly charge — floor ($)";
  return "Flat plan price or monthly minimum ($)";
};

const minimumPriceFieldHelper = (pricingTypeId: number | undefined): string => {
  if (pricingTypeId === PRICING_TYPE_FLAT_ID) {
    return "This is the full monthly amount for a flat plan (not a per-unit floor).";
  }
  if (pricingTypeId === PRICING_TYPE_PER_UNIT_ID) {
    return "Minimum billed per month; actual charges are max(units × rate, this value).";
  }
  return "Choose Pricing Type first: Flat = full monthly amount; Per Unit = monthly minimum.";
};

const monthlyPerUnitHelper = (pricingTypeId: number | undefined): string => {
  if (pricingTypeId === PRICING_TYPE_PER_UNIT_ID) {
    return "Per-unit rate before the monthly minimum is applied.";
  }
  if (pricingTypeId === PRICING_TYPE_FLAT_ID) {
    return "Not used for Flat pricing.";
  }
  return "Used when Pricing Type is Per Unit.";
};

export type PricingTierFormType = {
  lowerThreshold?: number;
  monthlyPrice?: number;
}

export type PricingFormType = {
  pricingTypeId?: number;
  thresholdTypeId?: number;
  minimumPrice?: number;
  unitThreshold?: number;
  annualDiscountPercent?: number;
  monthlyPricePerUnit?: number;
  tiers: PricingTierFormType[];
}

type Props = {
  initialData: PricingFormType;
  formType: "update" | "new";
  handleSubmit: FormikConfig<PricingFormType>["onSubmit"];
  sending: boolean;
  pricingTypes: Array<{ id: string; name: string }>;
  thresholdTypes: Array<{ id: string; name: string }>;
}

export const formSchema = y.object().shape({
  __typename: y.string().strip(true),
  id: y.string().strip(true),

  pricingTypeId: y
    .number()
    .nullable()
    .integer("Pricing Type ID must be a whole number"),

  thresholdTypeId: y
    .number()
    .nullable()
    .integer("Threshold Type ID must be a whole number"),

  minimumPrice: y
    .number()
    .nullable()
    .min(0, "Minimum price must be non-negative"),

  unitThreshold: y
    .number()
    .nullable()
    .integer("Unit threshold must be a whole number")
    .min(0, "Unit threshold must be non-negative"),

  annualDiscountPercent: y
    .number()
    .nullable()
    .min(0, "Annual discount percent must be non-negative")
    .max(100, "Annual discount percent cannot exceed 100%"),

  monthlyPricePerUnit: y
    .number()
    .nullable()
    .min(0, "Monthly price per unit must be non-negative"),

  tiers: y.array().of(
    y.object().shape({
      lowerThreshold: y
        .number()
        .required("Unit cutoff is required")
        .integer("Unit cutoff must be a whole number")
        .min(0, "Unit cutoff must be non-negative"),
      monthlyPrice: y
        .number()
        .required("Tier monthly price is required")
        .min(0, "Tier monthly price must be non-negative"),
    }),
  ),
});

const PricingForm: React.FC<Props> = (props: Props) => (
  <div>
    <Formik
      initialValues={props.initialData}
      onSubmit={props.handleSubmit}
      validationSchema={formSchema}
      render={(formik) => {
        const { isSubmitting, values } = formik;
        const { pricingTypeId } = values;

        return (
          <Form>
            <Grid container spacing={3} sx={{ mb: "30px" }}>
              <Grid size={{ xs: 12, md: 7 }}>
                <Grid container spacing={2} className="card">
                  <Grid size={{ xs: 12 }}>
                    <Typography>
                      Choose{" "}
                      <Typography component="span" fontWeight={700}>
                        Pricing Type
                      </Typography>{" "}
                      first. Flat plans use one monthly amount; Per Unit plans use a rate per
                      unit plus an optional monthly minimum.
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <Grid size={{ xs: 12, md: 10 }}>
                      <MaterialFormikSelectAutocomplete
                        id="pricingTypeId"
                        margin="none"
                        name="pricingTypeId"
                        placeholder="Pricing Type"
                        title="Pricing Type"
                        options={props.pricingTypes.map(type => ({
                          value: parseInt(type.id, 10),
                          label: type.name,
                        }))}
                      />
                    </Grid>
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <Grid size={{ xs: 12, md: 10 }}>
                      <MaterialFormikSelectAutocomplete
                        id="thresholdTypeId"
                        margin="none"
                        name="thresholdTypeId"
                        placeholder="Threshold Type"
                        title="Threshold Type"
                        options={props.thresholdTypes.map(type => ({
                          value: parseInt(type.id, 10),
                          label: type.name,
                        }))}
                      />
                    </Grid>
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <Grid size={{ xs: 12, md: 10 }}>
                      <Input
                        margin="none"
                        label={minimumPriceFieldLabel(pricingTypeId)}
                        name="minimumPrice"
                        type="number"
                        inputProps={{ step: "0.01", min: "0" }}
                        helperText={minimumPriceFieldHelper(pricingTypeId)}
                      />
                    </Grid>
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <Grid size={{ xs: 12, md: 10 }}>
                      <Input
                        margin="none"
                        label="Unit Threshold"
                        name="unitThreshold"
                        type="number"
                        inputProps={{ step: "1", min: "0" }}
                      />
                    </Grid>
                  </Grid>

                  <PricingTiersFields
                    formik={formik}
                    monthlyPerUnitHelper={monthlyPerUnitHelper(pricingTypeId)}
                  />

                  <Grid size={{ xs: 12 }}>
                    <Grid size={{ xs: 12, md: 10 }}>
                      <Input
                        margin="none"
                        label="Annual Discount Percent (%)"
                        name="annualDiscountPercent"
                        type="number"
                        inputProps={{ step: "1", min: "0", max: "100" }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              <Grid size={{ xs: 12, md: 5 }}>
                <PricingCalculator values={values} />
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
              <HistoryBackButton marginRight size="L" />
              <SubmitButton
                isWide
                text="Submit"
                loadingText="Sending"
                isLoading={isSubmitting}
              />
            </Grid>
          </Form>
        );
      }}
    />
  </div>
);

export default PricingForm;
